import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/auth'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api/admin',
  timeout: 30000,
})

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (
      (error.response?.status === 401 &&
        !error.config?.url?.endsWith('/auth/login')) ||
      (error.response?.status === 403 &&
        error.config?.url?.endsWith('/auth/me'))
    ) {
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

export interface ApiResponse<T> {
  code: number
  data?: T
  message?: string
}

export default apiClient

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 413) return '上传内容过大，请检查封面和试稿文件后重试（总量不超过 40 MB）'
    return (
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      '服务连接失败，请稍后重试'
    )
  }
  return error instanceof Error ? error.message : '操作失败'
}

export async function dataOf<T>(
  request: Promise<{ data: ApiResponse<T> }>,
): Promise<T> {
  const { data } = await request
  if (data.code !== 0 || data.data === undefined)
    throw new Error(data.message || '操作失败')
  return data.data
}
